"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ManagerAssistantChat = void 0;
const react_1 = __importStar(require("react"));
const react_2 = require("@assistant-ui/react");
const marked_react_1 = __importDefault(require("marked-react"));
const chart_js_1 = require("chart.js");
const react_chartjs_2_1 = require("react-chartjs-2");
const _ui_1 = require("@ui");
const classnames_1 = __importDefault(require("classnames"));
const store_1 = __importStar(require("@app/store"));
const image_1 = require("@app/image");
const config_1 = require("@app/const/config");
const util_1 = require("@app/util");
const maybe_unwrap_1 = require("@app/util/apollo/maybe-unwrap");
const use_modal_1 = require("@app/util/modal/use-modal");
const employee_card_v2_1 = require("@app/component/modals/employee-card-v2/employee-card-v2");
const onboarding_assistant_prompt_1 = require("@app/component/pages/onboarding/util/onboarding-assistant-prompt");
const manager_assistant_1 = require("@app/reducer/manager-assistant");
const onboarding_1 = require("@app/reducer/onboarding");
const assistant_attachment_chip_1 = require("./assistant-attachment-chip");
const assistant_banner_1 = require("./assistant-banner");
const assistant_history_panel_1 = require("./assistant-history-panel");
const assistant_quick_actions_1 = require("./assistant-quick-actions");
const assistant_usage_panel_1 = require("./assistant-usage-panel");
const assistant_usage_ring_1 = require("./assistant-usage-ring");
const manager_assistant_chat_persistence_1 = require("./manager-assistant-chat-persistence");
const use_assistant_quick_actions_1 = require("./use-assistant-quick-actions");
const use_assistant_history_1 = require("./use-assistant-history");
const assistant_topup_modal_1 = require("./assistant-topup-modal");
const use_assistant_usage_1 = require("./use-assistant-usage");
const use_can_buy_credits_1 = require("./use-can-buy-credits");
const use_dismiss_on_outside_1 = require("./use-dismiss-on-outside");
require("./manager-assistant-chat.scss");
// n8n manager-assistant workflow webhook (chatTrigger). Hardcoded like the employee
// embed (app/component/pages/my-calendar-2/employee-assistant-card.tsx) — it's a stable,
// pinned webhookId. The workflow + eval harness live in ../assistant-n8n/.
const WEBHOOK_URL = 'https://ops.dayswaps.top/webhook/fc7a0a70-4c9b-4029-9e44-33663ccacbb7/chat';
// The workflow's SECOND webhook: serves files the agent generated in the sandbox for download.
// The agent emits a `[[download:NAME]]` marker in its reply; we rewrite it (below) into a link
// to this endpoint scoped to the current session. Access is a capability URL — knowing the
// unguessable sessionId + filename is the auth; the response carries Content-Disposition:
// attachment, which makes the browser download it (even cross-origin) instead of navigating.
const DOWNLOAD_WEBHOOK_URL = 'https://ops.dayswaps.top/webhook/5e9d7c1a-3b2f-4c6d-9a8e-1f0b2c3d4e5f';
const DOWNLOAD_MARKER_RE = /\[\[download:([^\]\r\n]+)\]\]/g;
// Rewrite `[[download:report.xlsx]]` markers into markdown links to the download webhook. The
// markdown renderer (markdownRenderer.link) turns those links into a download button.
const withDownloadLinks = (text, sessionId) => text.replace(DOWNLOAD_MARKER_RE, (_match, rawName) => {
    const name = rawName.trim();
    const url = `${DOWNLOAD_WEBHOOK_URL}?session=${encodeURIComponent(sessionId)}&file=${encodeURIComponent(name)}`;
    return `[${name}](${url})`;
});
const parseChunk = (line) => {
    try {
        return JSON.parse(line);
    }
    catch {
        return null;
    }
};
// Non-streaming fallback: the webhook may answer with a single JSON object.
const extractText = (data) => {
    if (typeof data === 'string')
        return data;
    const obj = data;
    const value = obj?.output ?? obj?.text ?? obj?.message;
    return typeof value === 'string' ? value : '';
};
const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
});
// Accepted upload types. Images take the vision path (n8n `passthroughBinaryImages` forwards
// them to the model); the other types are what the Python sandbox can read (pandas/openpyxl/
// pypdf/python-docx + stdlib zip) — n8n writes them to the session folder and the agent
// processes them with run_python (Feature 4). We keep the raw File on every attachment so the
// chat adapter uploads it as multipart `files` (the @n8n/chat wire format), regardless of type.
const ATTACHMENT_ACCEPT = 'image/*,.csv,.xls,.xlsx,.pdf,.doc,.docx,.json,.txt,.zip';
// n8n buffers uploads in memory and the sandbox has 512 MB RAM — cap client-side.
const MAX_FILE_MB = 10;
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;
const isImageFile = (file) => file.type.startsWith('image/');
const fileAttachmentAdapter = {
    accept: ATTACHMENT_ACCEPT,
    async add({ file }) {
        if (file.size > MAX_FILE_BYTES) {
            util_1.notification.warn({
                message: (0, maybe_unwrap_1.unwrapMaybe)((0, util_1.t)('MAXIMUM_FILE_SIZE_WARNING', {
                    MAX_FILE_SIZE_TO_UPLOAD_IN_MB: MAX_FILE_MB,
                })),
            });
            // Reject the attachment — throwing keeps it out of the composer.
            throw new Error(`manager-assistant-chat: file exceeds ${MAX_FILE_MB} MB`);
        }
        return {
            id: (0, react_2.generateId)(),
            // Images get the in-composer/thumbnail preview + vision path; everything else is a
            // generic document chip whose content the agent reads server-side via run_python.
            type: isImageFile(file) ? 'image' : 'document',
            name: file.name,
            contentType: file.type,
            file,
            status: { type: 'requires-action', reason: 'composer-send' },
        };
    },
    async send(attachment) {
        if (isImageFile(attachment.file)) {
            const image = await fileToDataUrl(attachment.file);
            return {
                ...attachment,
                status: { type: 'complete' },
                content: [{ type: 'image', image }],
            };
        }
        // Non-image: no vision content part (the raw File still uploads via multipart in the
        // run adapter). The sent-message chip shows just the file name + a document icon.
        return { ...attachment, status: { type: 'complete' }, content: [] };
    },
    async remove() { },
};
// A month-wide selection must not balloon the payload — cap the ids we send. The full count
// is sent separately so the agent still knows the real size.
const MAX_SELECTED_IDS = 300;
// Read-only snapshot of what the manager is currently viewing / has selected / filtered, sent
// to the agent so it can act on "this week" / "the shifts I selected" etc. Sends ONLY what the
// agent can't derive from its own GraphQL tools — NOT bulk state (latency + the state holds the
// auth token and employee PII). Read lazily from the live store at send time so it reflects the
// screen at the moment the message is sent (no chat re-render on every calendar interaction).
//
// NOTE: open-modal context (e.g. which shift/employee card is open) and table context (active
// table + its sort/filter/page) are NOT here yet — that state lives in React (the modal engine's
// context + react-table component state), not Redux, so it isn't readable from the store. Adding
// it needs those source components to publish a small snapshot into `app/reducer/ui-context.ts`
// (which the onboarding checklist below already does). See ../assistant-n8n/PLAN.md.
const buildViewContext = () => {
    const { calendar, calendarMultiSelect, calendarFilters, loading, uiContext } = store_1.default.getState();
    const uri = window.location.pathname + window.location.search;
    const context = { uri };
    // Calendar/schedule-only fields — they only mean anything on the schedule page.
    if (uri.includes('/schedule')) {
        const sourceEvents = calendarMultiSelect?.sourceEvents ?? [];
        context.view = calendar?.view;
        context.date = calendar?.date;
        context.selectedEventIds = sourceEvents
            .map((event) => event.id)
            .slice(0, MAX_SELECTED_IDS);
        context.selectedEventCount = sourceEvents.length;
        context.selectedAction = calendarMultiSelect?.action ?? null;
        context.filters = (calendarFilters ?? []).map((filter) => ({
            peopleWithPositionId: filter.peopleWithPositionId,
            userId: filter.userId,
            type: filter.type,
            hideWarnings: filter.hideWarnings,
        }));
        // Workspace-wide count of unpublished (draft) future shifts — NOT scoped to the period on
        // screen. Only loaded on the schedule page; may be undefined briefly before it loads.
        // Skip it while a refetch is in flight: the value is about to change, and sending nothing
        // is better than sending a number that's mid-update and could mislead the agent.
        const countIsLoading = (loading ?? []).includes('load-changed-shifts-count');
        if ((0, util_1.isValue)(calendar?.changedShiftsCount) && !countIsLoading) {
            context.changedShiftsCount = calendar.changedShiftsCount;
        }
    }
    // The onboarding checklist, published by the page that draws it (app/reducer/ui-context.ts)
    // because its steps are derived in a component hook rather than held in the store. No uri
    // check needed: the page clears the snapshot when it unmounts, so its presence already
    // means the manager is looking at it.
    if ((0, util_1.isValue)(uiContext?.onboarding)) {
        context.onboarding = uiContext.onboarding;
    }
    return context;
};
// ---- Agent message conventions ----
// The agent embeds structured bits in its markdown (taught in the base system prompt,
// ../assistant-n8n/skills/00-base-system-prompt.md); the custom renderer
// below intercepts them:
//   [Name](employee:<userId>)     -> clickable name that opens the employee card
//   ```suggestions ["Ano", ...]   -> quick-reply buttons (shown on the last message only)
//   ```chart {type, labels, ...}  -> a small chart rendered with chart.js
// Plain markdown conventions (instead of custom tags) survive streaming gracefully and
// degrade to readable text in clients that don't implement them (e.g. the mobile app).
const EMPLOYEE_LINK_SCHEME = 'employee:';
const SUGGESTIONS_LANG = 'suggestions';
const CHART_LANG = 'chart';
// Rendered as an <a>, not a <button>: an anchor is a true inline element, so long names
// wrap across lines in the narrow sidebar and the hover/click area follows the text
// exactly (an inline-block button is atomic — it can't wrap, and its hit-box doesn't
// always match the visible text inside list items).
const EmployeeLink = ({ userId, children }) => {
    const { openModal } = (0, use_modal_1.useModal)();
    return (react_1.default.createElement("a", { href: '#', role: 'button', className: 'ds-ma-employee-link', onClick: (event) => {
            event.preventDefault();
            openModal(employee_card_v2_1.EmployeeCardV2, { userId });
        } }, children));
};
// Quick-reply strings from a ```suggestions block. Returns null while the block is still
// streaming in (partial JSON) or malformed — the block then renders as nothing.
const parseSuggestions = (code) => {
    try {
        const parsed = JSON.parse(code);
        if (!Array.isArray(parsed))
            return null;
        const items = parsed.filter((item) => typeof item === 'string');
        return items.length > 0 ? items : null;
    }
    catch {
        return null;
    }
};
// Whether sending is blocked, for the message subtree.
//
// The agent's suggestion pills are rendered by the markdown renderer, several layers below the
// session that knows this — a `code` override inside `MarkdownText` inside a message. Threading
// a prop down through marked-react is not possible, so this is the one piece of sidebar state
// that travels by context. Default false: a tree with no provider (nothing does that today) is
// not blocked.
const ComposerBlockedContext = react_1.default.createContext(false);
// Quick-reply buttons the agent offers when it asks a question. Only rendered on the last
// assistant message and only once the run has finished — clicking sends the text as a
// regular user message.
const SuggestedReplies = ({ code }) => {
    const threadRuntime = (0, react_2.useThreadRuntime)();
    const isLast = (0, react_2.useMessage)((message) => message.isLast);
    const isRunning = (0, react_2.useThread)((thread) => thread.isRunning);
    // A pill sends by calling the runtime directly, so it is the ONE way to send that does not
    // go through the composer — and it therefore has to be blocked explicitly. Left visible and
    // inert rather than hidden, like the chip strip: the manager can still read what was offered,
    // and options vanishing from a message they already saw is worse than options they cannot
    // press. Without this, a click on a stale pill in an expired conversation really does send,
    // and once a quota block is possible it would spend a conversation the workspace has not got.
    const isBlocked = react_1.default.useContext(ComposerBlockedContext);
    const items = parseSuggestions(code);
    if (!(0, util_1.isValue)(items) || !isLast || isRunning)
        return null;
    return (react_1.default.createElement("div", { className: 'ds-ma-suggestions' }, items.map((item, idx) => (react_1.default.createElement("button", { key: `${idx}-${item}`, type: 'button', className: 'ds-ma-suggestion', "data-testid": 'ds-ma-suggestion', disabled: isBlocked, onClick: () => threadRuntime.append(item) }, item)))));
};
// Chart support: the agent emits a deliberately MINIMAL spec (not a raw chart.js config) —
// we build the real config here so the app controls colors and sizing.
chart_js_1.Chart.register(chart_js_1.ArcElement, chart_js_1.BarElement, chart_js_1.CategoryScale, chart_js_1.Legend, chart_js_1.LinearScale, chart_js_1.LineElement, chart_js_1.PointElement, chart_js_1.Tooltip);
// Chart.js draws its labels onto a canvas, which inherits nothing from CSS — without this
// it keeps its own default stack ('Helvetica Neue', Helvetica, Arial) while the rest of the
// app is in Roboto.
chart_js_1.Chart.defaults.font.family = "'Roboto', 'Arial', 'Helvetica', sans-serif";
const CHART_TYPES = ['bar', 'line', 'pie'];
// Values from app/style/const.scss ($color-primary, $color-orange, $color-green,
// $color-red, $color-dark-blue-1, $color-gray) — SCSS variables aren't readable from TS.
const CHART_COLORS = [
    '#0045ff',
    '#ff8a00',
    '#31a847',
    '#f44c4c',
    '#2a4284',
    '#495063',
];
const CHART_TICK = '#495063'; // $color-gray — axis ticks/labels
const CHART_GRID = '#efefef'; // $color-light-gray-2 — grid lines
const parseChartSpec = (code) => {
    try {
        const spec = JSON.parse(code);
        if (!CHART_TYPES.includes(spec?.type))
            return null;
        if (!Array.isArray(spec.labels) || !Array.isArray(spec.datasets))
            return null;
        if (spec.datasets.length === 0 ||
            !spec.datasets.every((dataset) => Array.isArray(dataset?.data))) {
            return null;
        }
        return spec;
    }
    catch {
        return null;
    }
};
// Bar charts with more labels than this flip to horizontal (names on the y-axis, chart
// grows downward) — many vertical bars don't fit the narrow sidebar readably.
const HORIZONTAL_BAR_THRESHOLD = 8;
const AgentChart = ({ code }) => {
    const spec = parseChartSpec(code);
    // Still streaming in, or malformed → render nothing (never raw JSON).
    if (!(0, util_1.isValue)(spec))
        return null;
    const horizontal = spec.type === 'bar' && spec.labels.length > HORIZONTAL_BAR_THRESHOLD;
    const data = {
        labels: spec.labels,
        datasets: spec.datasets.map((dataset, idx) => {
            const color = CHART_COLORS[idx % CHART_COLORS.length];
            if (spec.type === 'pie') {
                return {
                    label: dataset.label,
                    data: dataset.data,
                    // One color per slice; white gaps between slices read cleaner than touching arcs.
                    backgroundColor: dataset.data.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
                    borderColor: '#fff',
                    borderWidth: 2,
                };
            }
            if (spec.type === 'line') {
                // A line, not a filled area — a solid backgroundColor would flood the plot.
                return {
                    label: dataset.label,
                    data: dataset.data,
                    borderColor: color,
                    backgroundColor: color,
                    fill: false,
                    tension: 0.3,
                    pointRadius: 3,
                };
            }
            return {
                label: dataset.label,
                data: dataset.data,
                backgroundColor: color,
                borderRadius: 4,
            };
        }),
    };
    // Legend only when it carries information (pie slices, or named datasets on bar/line).
    const showLegend = spec.type === 'pie' || spec.datasets.some((dataset) => (0, util_1.isValue)(dataset.label));
    const baseOptions = {
        responsive: true,
        // Sizing comes from the wrapper's fixed height (SCSS / inline), not chart.js's
        // per-type default aspect ratio — predictable in the narrow sidebar.
        maintainAspectRatio: false,
        layout: { padding: 4 },
        plugins: {
            // chart.js's plugin registry is a SINGLETON, and the calendar Statistics chart
            // registers chartjs-plugin-datalabels globally — so without this our bars inherit
            // its low-contrast value labels. Off here; exact values are on hover (tooltip).
            datalabels: { display: false },
            legend: {
                display: showLegend,
                position: 'bottom',
                labels: { boxWidth: 12, boxHeight: 12, padding: 12, font: { size: 11 } },
            },
        },
    };
    const axis = {
        ticks: { font: { size: 11 }, color: CHART_TICK },
        grid: { color: CHART_GRID },
    };
    const cartesianOptions = {
        ...baseOptions,
        indexAxis: horizontal ? 'y' : 'x',
        scales: {
            x: { ...axis, beginAtZero: true },
            y: { ...axis, beginAtZero: true },
        },
    };
    return (react_1.default.createElement("div", { className: 'ds-ma-chart', 
        // Horizontal bars grow with label count (set here since it's dynamic); the other
        // types get a fixed height from SCSS.
        style: horizontal ? { height: `${spec.labels.length * 1.6 + 3}rem` } : undefined },
        spec.type === 'bar' && react_1.default.createElement(react_chartjs_2_1.Bar, { data: data, options: cartesianOptions }),
        spec.type === 'line' && react_1.default.createElement(react_chartjs_2_1.Line, { data: data, options: cartesianOptions }),
        spec.type === 'pie' && react_1.default.createElement(react_chartjs_2_1.Pie, { data: data, options: baseOptions })));
};
// Custom marked-react renderer intercepting the agent's message conventions; everything
// not matched falls through to output identical to marked-react's default. Methods must be
// regular functions (not arrows): marked-react binds them to the renderer instance, which
// provides `this.elementId` — its stable per-parse key counter (a module-level counter
// would change keys on every stream chunk and remount the whole message).
const markdownRenderer = {
    link(href, text) {
        if (href.startsWith(EMPLOYEE_LINK_SCHEME)) {
            const userId = href.slice(EMPLOYEE_LINK_SCHEME.length);
            return (react_1.default.createElement(EmployeeLink, { key: `ds-ma-md-${this.elementId}`, userId: userId }, text));
        }
        // Agent-generated file download (from a [[download:NAME]] marker, rewritten upstream). The
        // `download` attr + the endpoint's Content-Disposition make it download rather than navigate.
        if (href.startsWith(DOWNLOAD_WEBHOOK_URL)) {
            return (react_1.default.createElement("a", { key: `ds-ma-md-${this.elementId}`, className: 'ds-ma-download-link', href: href, download: true },
                react_1.default.createElement(_ui_1.Icon, { ico: _ui_1.Icon.ICONS.export, size: _ui_1.Icon.SIZES.SMALL }),
                text));
        }
        // Same as the default renderer with openLinksInNewTab (the default we were getting).
        return (react_1.default.createElement("a", { key: `ds-ma-md-${this.elementId}`, href: href, target: '_blank', rel: 'noreferrer' }, text));
    },
    code(code, lang) {
        if (lang === SUGGESTIONS_LANG && typeof code === 'string') {
            return react_1.default.createElement(SuggestedReplies, { key: `ds-ma-md-${this.elementId}`, code: code });
        }
        if (lang === CHART_LANG && typeof code === 'string') {
            return react_1.default.createElement(AgentChart, { key: `ds-ma-md-${this.elementId}`, code: code });
        }
        // Same output as the default renderer (pre > code.lang-*).
        return (react_1.default.createElement("pre", { key: `ds-ma-md-${this.elementId}` },
            react_1.default.createElement("code", { className: (0, util_1.isValue)(lang) ? `lang-${lang}` : undefined }, code)));
    },
};
// Render assistant text as markdown (mirrors the old ChatbotUI). User text stays plain.
const MarkdownText = ({ text }) => (react_1.default.createElement(marked_react_1.default, { isInline: false, gfm: true, breaks: true, renderer: markdownRenderer }, text));
const UserMessage = () => (react_1.default.createElement(react_2.MessagePrimitive.Root, { className: 'ds-ma-message ds-ma-message-user' },
    react_1.default.createElement("div", { className: 'ds-ma-message-text', "data-testid": 'ds-ma-message-user' },
        react_1.default.createElement(react_2.MessagePrimitive.Attachments, { components: { Image: SentAttachment, Attachment: SentAttachment } }),
        react_1.default.createElement(react_2.MessagePrimitive.Parts, null))));
const AssistantMessage = () => (react_1.default.createElement(react_2.MessagePrimitive.Root, { className: 'ds-ma-message ds-ma-message-assistant' },
    react_1.default.createElement("div", { className: 'ds-ma-message-text', "data-testid": 'ds-ma-message-assistant' },
        react_1.default.createElement(react_2.MessagePrimitive.Parts, { components: { Text: MarkdownText } }))));
// Thumbnail for a sent attachment — render the image data URL from the attachment content
// directly (the built-in unstable_Thumb didn't display). Renders nothing if there's no image.
const SentThumb = () => {
    const image = (0, react_2.useAttachment)((attachment) => {
        const part = attachment.content?.find((p) => p.type === 'image');
        return part?.type === 'image' ? part.image : undefined;
    });
    return (0, util_1.isValue)(image) ? (react_1.default.createElement("img", { className: 'ds-ma-sent-thumb', src: image, alt: '' })) : null;
};
// Attachment shown inside a SENT user message (thumbnail/icon + filename, no remove). Name is
// the guaranteed-visible fallback so a file-only message (no accompanying text) still shows up.
// Images render a thumbnail; other documents get a generic file icon.
const SentAttachment = () => {
    const isImage = (0, react_2.useAttachment)((attachment) => attachment.type === 'image');
    return (react_1.default.createElement(react_2.AttachmentPrimitive.Root, { className: 'ds-ma-sent-attachment' },
        isImage ? (react_1.default.createElement(SentThumb, null)) : (react_1.default.createElement(_ui_1.Icon, { ico: 'attachment', size: _ui_1.Icon.SIZES.SMALL, color: _ui_1.Icon.COLORS.WHITE })),
        react_1.default.createElement(react_2.AttachmentPrimitive.Name, null)));
};
// Sends the onboarding step's message as the manager's own first turn. `append` runs it
// through the same adapter a typed message uses, so the agent really answers — which is the
// whole point: the reply and its quick replies are the agent's, not ours.
//
// Lives inside the runtime provider because that's the only place `useThreadRuntime` works,
// and fires exactly once per mount: the remount that carries a new prompt is what makes the
// next send happen, never a re-render.
const ManagerAssistantChatSeed = ({ prompt, onConsumed, }) => {
    const threadRuntime = (0, react_2.useThreadRuntime)();
    const hasSentRef = (0, react_1.useRef)(false);
    (0, react_1.useEffect)(() => {
        if (!(0, util_1.isValue)(prompt) || hasSentRef.current)
            return;
        hasSentRef.current = true;
        threadRuntime.append(prompt);
        onConsumed();
    }, [prompt, onConsumed, threadRuntime]);
    return null;
};
// Delivers a quick action clicked on the COLLAPSED RAIL into the composer. The rail is
// mounted outside <AssistantRuntimeProvider> and unmounts during the very expand that has to
// deliver the prompt, so it can neither reach the composer nor stay around to watch it land;
// it parks the prompt on the column instead, and this is the null-rendering component, inside
// the provider, that spends it. Same shape as the seed above and the depth probe below, and
// for the same reason: the runtime's context is only in scope in here.
//
// It INSERTS and never sends, exactly as a chip in the strip does — sending would spend a
// message from the business's quota without the manager choosing to. Where it differs from
// the strip is a DRAFT: the strip replaces (the library's `clearComposer` default), this
// APPENDS, with the library's own separator rule. The reasoning is in the rail's comment in
// `manager-assistant-column.tsx` — do not "align" the two without reading it.
const ManagerAssistantChatRailPrompt = ({ prompt, onConsumed, onInserted }) => {
    const composerRuntime = (0, react_2.useComposerRuntime)();
    (0, react_1.useEffect)(() => {
        if (!(0, util_1.isValue)(prompt))
            return;
        // Focus first, THEN the text — the order the strip already proves: `createActionButton`
        // composes the consumer's onClick ahead of the library's own, so the strip focuses an
        // empty textarea and the value arrives afterwards. It matters because setting a
        // textarea's value moves the caret to the end of it, which is where the manager wants to
        // carry on typing; focusing after the value had landed would leave the caret at 0.
        onInserted();
        const { text } = composerRuntime.getState();
        composerRuntime.setText(text.trim() ? `${text} ${prompt}` : prompt);
        onConsumed();
    }, [prompt, composerRuntime, onConsumed, onInserted]);
    return null;
};
const ManagerAssistantChatPersistence = ({ sessionId, lastExchangeAt, userId, workspaceId, onboardingStep }) => {
    const messages = (0, react_2.useThread)((thread) => thread.messages);
    const isRunning = (0, react_2.useThread)((thread) => thread.isRunning);
    (0, react_1.useEffect)(() => {
        if (isRunning ||
            !(0, util_1.isValue)(userId) ||
            !(0, util_1.isValue)(workspaceId) ||
            !(0, util_1.isValue)(lastExchangeAt) ||
            messages.length === 0) {
            return;
        }
        (0, manager_assistant_chat_persistence_1.saveManagerAssistantChat)({
            sessionId,
            userId,
            workspaceId,
            lastExchangeAt,
            onboardingStep,
            messages,
        });
    }, [
        isRunning,
        lastExchangeAt,
        messages,
        onboardingStep,
        sessionId,
        userId,
        workspaceId,
    ]);
    return null;
};
const ManagerAssistantChatSession = ({ onCollapse, railPrompt, onRailPromptConsumed, onComposerBlockedChange, isPlanningVisible, onNewChat, onExchange, sessionId, initialMessages, lastExchangeAt, isExpired, userId, workspaceId, onboardingStep, seedPrompt, onSeedConsumed, }) => {
    const { location: { hostname }, } = window;
    const { backend } = (0, config_1.getEndpoints)(hostname);
    const auth = (0, store_1.useRootSelector)((state) => state.auth);
    const me = (0, store_1.useRootSelector)((state) => state.me);
    const ws = (0, store_1.useRootSelector)((state) => state.workspaces).find((w) => w.id === workspaceId);
    // The quick-action chips above the composer. The collapsed rail renders the same list as
    // icons — one definition, in the hook, so the two surfaces cannot drift apart.
    const quickActions = (0, use_assistant_quick_actions_1.useAssistantQuickActions)(workspaceId);
    // The workspace's credit balance, in conversations, asked for THIS session: the server answers
    // `blocked` for the conversation on screen, and a session it has already charged is never
    // blocked — so a balance that runs out mid-conversation cannot cut it off. Re-read after each
    // exchange, because the n8n workflow charges on the first turn and the ring is on screen while
    // it happens. `undefined` is "no usage data" — a request in flight or a failure — and shows
    // nothing and restricts nothing.
    const { usage: assistantUsage, refresh: refreshUsage } = (0, use_assistant_usage_1.useAssistantUsage)(workspaceId, sessionId);
    // Tells the rest of the app a reply has finished — the agent may have just changed data (the
    // dashboard's alerts reload on it). Every reply counts: the n8n response doesn't say whether
    // a tool wrote anything.
    const dispatch = (0, store_1.useRootDispatch)();
    // The ring and the panel are the same fact: a count plus a ceiling to measure it against.
    // A workspace holding no package has no ceiling, so it gets neither.
    const usageWithLimit = (0, util_1.isValue)(assistantUsage) && assistantUsage.hasLimit ? assistantUsage : null;
    // The way out of a spent balance, offered only to whoever may spend the organization's money.
    // `undefined` for everyone else, which is what removes the button from the panel and the
    // banner rather than leaving one that answers with a permission error.
    const canBuyCredits = (0, use_can_buy_credits_1.useCanBuyCredits)();
    const { openModal } = (0, use_modal_1.useModal)();
    const onBuyCredits = canBuyCredits && (0, util_1.isValue)(workspaceId)
        ? () => openModal(assistant_topup_modal_1.AssistantTopupModal, { workspaceId })
        : undefined;
    // Read straight off the server's verdict, never recomputed from used/limit here: the charge in
    // the workflow applies the same gates, and two independent `used >= limit` comparisons is
    // exactly how a client and a server stop agreeing about whether someone may send.
    const isQuotaBlocked = (0, util_1.isValue)(assistantUsage) && assistantUsage.blocked;
    // The composer's textarea. A chip fills the composer through the library's primitive, which
    // has no focus() of its own (and the ComposerRuntime exposes none either), so the caret has
    // to be put back by hand — otherwise the manager has to click the input before adjusting the
    // prompt the chip just inserted.
    const composerInputRef = (0, react_1.useRef)(null);
    const focusComposer = (0, react_1.useCallback)(() => {
        composerInputRef.current?.focus();
    }, []);
    // The bridge below takes a required callback, and the prop it forwards to is optional (the
    // standalone chat has no rail). Wrapped rather than defaulted so the identity stays stable
    // across renders — it is an effect dependency in there.
    const handleRailPromptConsumed = (0, react_1.useCallback)(() => {
        onRailPromptConsumed?.();
    }, [onRailPromptConsumed]);
    // The sidebar's two overlays as ONE value rather than a boolean each: with a boolean each,
    // "both open" is representable and has to be prevented by hand at every call site. Opening
    // either closes the other by construction.
    const [openSurface, setOpenSurface] = (0, react_1.useState)(null);
    const toggleSurface = (0, react_1.useCallback)((surface) => {
        setOpenSurface((current) => (current === surface ? null : surface));
    }, []);
    const closeSurface = (0, react_1.useCallback)(() => setOpenSurface(null), []);
    const isUsagePanelOpen = openSurface === 'USAGE';
    const isHistoryOpen = openSurface === 'HISTORY';
    const usagePanelRef = (0, react_1.useRef)(null);
    const usageRingRef = (0, react_1.useRef)(null);
    const historyPanelRef = (0, react_1.useRef)(null);
    const historyButtonRef = (0, react_1.useRef)(null);
    // The manager's archived conversations. Asked for only once the panel is opened, and dropped
    // on a workspace change so one workspace's chats can never appear under another's.
    const history = (0, use_assistant_history_1.useAssistantHistory)(workspaceId, isHistoryOpen);
    // The trigger counts as "inside" in both cases: it is what toggles the panel shut, so a press
    // on it must reach its own handler rather than be treated as a press outside.
    (0, use_dismiss_on_outside_1.useDismissOnOutside)(isUsagePanelOpen, [usagePanelRef, usageRingRef], closeSurface);
    (0, use_dismiss_on_outside_1.useDismissOnOutside)(isHistoryOpen, [historyPanelRef, historyButtonRef], closeSurface);
    // Every way of sending is gated by one value, so a new reason is one more term here rather
    // than six edits at the call sites. Attaching is gated too: a file on a message that cannot
    // be sent is a dead end.
    //
    // Blocking a NEW conversation is the whole design — the server's `blocked` is never true for
    // a conversation already paid for, so nobody is cut off mid-sentence. The client only
    // disables; the charge inside the n8n workflow is what actually enforces the balance, and a
    // turn it refuses comes back as a one-sentence reply, after which the re-read locks this.
    const isComposerBlocked = isExpired || isQuotaBlocked;
    // Up to the column, whose collapsed rail carries the same chips and must go inert with them.
    (0, react_1.useEffect)(() => {
        onComposerBlockedChange?.(isComposerBlocked);
    }, [isComposerBlocked, onComposerBlockedChange]);
    // Why the manager cannot send, and the one thing that fixes it. One value rather than a
    // ternary at the render site. Expired wins: it makes THIS conversation unusable and its
    // action is the only way on, whereas a spent balance is a standing fact with nothing to
    // press — the date the credits lapse is in the panel behind the ring.
    //
    // The server's refusal reason is preferred over the balance when there is one, because the
    // two disagree in exactly one case worth naming: a personal daily cap on a workspace whose
    // pool is far from empty, where "credits are used up" would send the manager hunting for a
    // billing problem that does not exist.
    const activeBanner = isExpired
        ? { variant: assistant_banner_1.ASSISTANT_BANNERS.EXPIRED, onAction: onNewChat }
        : isQuotaBlocked && assistantUsage.blockedReason === 'DAILY_LIMIT'
            ? {
                variant: assistant_banner_1.ASSISTANT_BANNERS.DAILY_LIMIT,
                testid: 'ai-banner-daily-limit',
            }
            : isQuotaBlocked
                ? {
                    variant: assistant_banner_1.ASSISTANT_BANNERS.QUOTA,
                    onAction: onBuyCredits,
                    testid: 'ai-banner-quota',
                }
                : null;
    // Stable session id for this restored chat → keys the n8n memoryBufferWindow
    // (server-side history), same role as @n8n/chat's sessionId. We send only the
    // latest user message; n8n remembers the rest while the Simple Memory TTL lives.
    const sessionIdRef = (0, react_1.useRef)(sessionId);
    // The adapter closure reads the latest token/metadata via a ref (token can refresh).
    const metaRef = (0, react_1.useRef)({
        token: auth,
        apiUrl: backend,
        userId: me.id,
        workspaceId,
        timezone: ws?.timezone,
        currentLanguage: me.language,
        onboardingStep,
    });
    metaRef.current = {
        token: auth,
        apiUrl: backend,
        userId: me.id,
        workspaceId,
        timezone: ws?.timezone,
        currentLanguage: me.language,
        onboardingStep,
    };
    const adapter = (0, react_1.useMemo)(() => ({
        async *run({ messages, abortSignal }) {
            // The conversation is paid for inside the n8n workflow, not here: it charges the
            // workspace before running the agent (idempotent per sessionId, so one charge covers the
            // whole session) and answers a refused turn with a one-sentence reply. This side only
            // re-reads the balance once the exchange is over — see the end of this function.
            // Exchanges (not clicks/typing) are what reset the server-side memory TTL —
            // stamp at both ends of the run (n8n reads memory at start, saves after reply).
            onExchange();
            const lastMessage = messages[messages.length - 1];
            const chatInput = lastMessage.content
                .filter((part) => part.type === 'text')
                .map((part) => part.text)
                .join('\n');
            // sessionId is also embedded in metadata: on multipart (file) requests the top-level
            // sessionId form field doesn't reliably reach the workflow as `$json.sessionId`, but
            // metadata does (same channel the token rides) — the memory keys off metadata.sessionId
            // so multi-turn continuity survives across image-upload turns.
            const metadata = {
                ...metaRef.current,
                viewContext: buildViewContext(),
                sessionId: sessionIdRef.current,
            };
            const attachments = lastMessage.role === 'user' ? lastMessage.attachments : [];
            const files = attachments
                .map((attachment) => attachment.file)
                .filter((file) => file instanceof File);
            let response;
            if (files.length) {
                // Image upload → multipart, matching @n8n/chat exactly so the SAME backend (and the
                // mobile app) handle it identically. No Content-Type header — the browser sets the
                // multipart boundary. metadata goes as a JSON string (n8n parses it server-side).
                //
                // n8n's buffer-window memory does NOT persist a turn whose chatInput is empty — so an
                // image sent with no accompanying text would vanish from the conversation on the next
                // turn. Fall back to the file name(s) so the upload turn (and the agent's reading of the
                // image) is saved to memory. The visible user message is unaffected (it renders the
                // thumbnail).
                const chatInputForSend = chatInput.trim().length > 0
                    ? chatInput
                    : files.map((file) => file.name).join(', ');
                const form = new FormData();
                form.append('action', 'sendMessage');
                form.append('sessionId', sessionIdRef.current);
                form.append('chatInput', chatInputForSend);
                form.append('metadata', JSON.stringify(metadata));
                for (const file of files)
                    form.append('files', file);
                response = await fetch(WEBHOOK_URL, {
                    method: 'POST',
                    signal: abortSignal,
                    body: form,
                });
            }
            else {
                response = await fetch(WEBHOOK_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    signal: abortSignal,
                    body: JSON.stringify({
                        action: 'sendMessage',
                        sessionId: sessionIdRef.current,
                        chatInput,
                        metadata,
                    }),
                });
            }
            // Rewrite the agent's [[download:NAME]] markers into links scoped to this session.
            const rewrite = (value) => withDownloadLinks(value, sessionIdRef.current);
            if (!(0, util_1.isValue)(response.body)) {
                const data = await response.json();
                yield { content: [{ type: 'text', text: rewrite(extractText(data)) }] };
                onExchange();
                refreshUsage();
                dispatch((0, manager_assistant_1.repliedManagerAssistant)());
                return;
            }
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let text = '';
            // The whole body, kept alongside the line parsing for the non-streaming fallback below.
            let raw = '';
            // n8n emits one begin→item…→end cycle per model round, and a turn with tool calls has
            // several: the short lines the model writes before each tool call, then the answer.
            // Those pre-tool lines are the one thing no prompt could remove on any model (ids, UTC
            // values, and on Sonnet 5.5 an "I hadn't verified…" note before it goes and checks), so
            // each segment is REPLACED by the next one as soon as that one starts streaming: the
            // pre-tool line stays on screen as a status while the tool runs, and only the last
            // segment remains in the bubble. The eval runner judges the same last segment.
            let segmentStarted = false;
            const flushLine = (line) => {
                const trimmed = line.trim();
                if (trimmed === '')
                    return null;
                const chunk = parseChunk(trimmed);
                if (!(0, util_1.isValue)(chunk))
                    return null;
                if (chunk.type === 'begin') {
                    segmentStarted = true;
                    return null;
                }
                if (chunk.type === 'item' && (0, util_1.isValue)(chunk.content)) {
                    if (segmentStarted) {
                        text = '';
                        segmentStarted = false;
                    }
                    text += chunk.content;
                    return { content: [{ type: 'text', text: rewrite(text) }] };
                }
                return null;
            };
            // eslint-disable-next-line no-constant-condition
            while (true) {
                const { done, value } = await reader.read();
                if (done)
                    break;
                const decoded = decoder.decode(value, { stream: true });
                raw += decoded;
                buffer += decoded;
                const lines = buffer.split('\n');
                buffer = lines.pop() ?? '';
                for (const line of lines) {
                    const update = flushLine(line);
                    if ((0, util_1.isValue)(update))
                        yield update;
                }
            }
            const tail = flushLine(buffer);
            if ((0, util_1.isValue)(tail))
                yield tail;
            // `response.body` is set even when the webhook answers with a single JSON object
            // instead of the begin/item/end protocol, so the check above never catches that case.
            // Without this the reply renders as an empty message — read the whole body as one
            // object when no `item` chunk ever arrived. parseChunk doubles as the JSON-or-null
            // parser here, and extractText tolerates null, so a non-JSON body just yields nothing.
            if (text === '') {
                const fallback = extractText(parseChunk(raw));
                if (fallback !== '')
                    yield { content: [{ type: 'text', text: rewrite(fallback) }] };
            }
            onExchange();
            // The workflow has charged (first turn) or refused (balance or daily cap) by now, and
            // either moves the figures: the ring updates, or the server's `blocked` for this session
            // flips and the composer locks under the reply that said why.
            refreshUsage();
            dispatch((0, manager_assistant_1.repliedManagerAssistant)());
        },
    }), [onExchange, refreshUsage, dispatch]);
    const runtime = (0, react_2.useLocalRuntime)(adapter, {
        initialMessages,
        adapters: { attachments: fileAttachmentAdapter },
    });
    return (react_1.default.createElement(react_2.AssistantRuntimeProvider, { runtime: runtime },
        react_1.default.createElement(ComposerBlockedContext.Provider, { value: isComposerBlocked },
            react_1.default.createElement(ManagerAssistantChatPersistence, { sessionId: sessionIdRef.current, lastExchangeAt: lastExchangeAt, userId: userId, workspaceId: workspaceId, onboardingStep: onboardingStep }),
            react_1.default.createElement(ManagerAssistantChatSeed, { prompt: seedPrompt, onConsumed: onSeedConsumed }),
            react_1.default.createElement(ManagerAssistantChatRailPrompt, { prompt: railPrompt, onConsumed: handleRailPromptConsumed, onInserted: focusComposer }),
            react_1.default.createElement("div", { className: 'ds-manager-assistant', "data-testid": 'ds-ma-chat', "data-onboarding-step": onboardingStep },
                react_1.default.createElement("div", { className: 'ds-ma-header' },
                    react_1.default.createElement("span", { className: 'ds-ma-header-logo' },
                        react_1.default.createElement(image_1.LogoWide, null)),
                    react_1.default.createElement(_ui_1.Text, { className: 'ds-ma-header-title', weight: _ui_1.Text.WEIGHTS.MEDIUM, type: _ui_1.Text.TYPES.BODY_LARGE }, (0, util_1.t)('ASSISTANT.title')),
                    react_1.default.createElement("div", { className: 'ds-ma-header-actions' },
                        react_1.default.createElement("button", { type: 'button', className: 'ds-ma-new-chat', onClick: onNewChat, "aria-label": (0, util_1.t)('ASSISTANT_RESTART_CONVERSATION') ?? undefined, title: (0, util_1.t)('ASSISTANT_RESTART_CONVERSATION') ?? undefined },
                            react_1.default.createElement(_ui_1.Icon, { ico: _ui_1.Icon.ICONS.plus, color: _ui_1.Icon.COLORS.INHERIT })),
                        react_1.default.createElement("button", { type: 'button', className: 'ds-ma-history', ref: historyButtonRef, onClick: () => toggleSurface('HISTORY'), "aria-haspopup": 'dialog', "aria-expanded": isHistoryOpen, "aria-label": (0, util_1.t)('HISTORY') ?? undefined, title: (0, util_1.t)('HISTORY') ?? undefined, "data-testid": 'ai-history-toggle' },
                            react_1.default.createElement(_ui_1.Icon, { ico: _ui_1.Icon.ICONS.history, size: _ui_1.Icon.SIZES.SMALL, color: _ui_1.Icon.COLORS.INHERIT })),
                        (0, util_1.isValue)(onCollapse) && (react_1.default.createElement("button", { type: 'button', className: 'ds-ma-collapse', onClick: onCollapse, "data-testid": 'ai-sidebar-toggle', "aria-label": (0, util_1.t)('HIDE') ?? undefined, title: (0, util_1.t)('HIDE') ?? undefined },
                            react_1.default.createElement(_ui_1.Icon, { ico: _ui_1.Icon.ICONS.layoutSidebarRightExpand, color: _ui_1.Icon.COLORS.INHERIT }))))),
                react_1.default.createElement(react_2.ThreadPrimitive.Root, { asChild: true },
                    react_1.default.createElement(react_2.ComposerPrimitive.AttachmentDropzone, { className: 'ds-ma-thread', disabled: isComposerBlocked },
                        react_1.default.createElement(react_2.ThreadPrimitive.Viewport, { className: 'ds-ma-viewport' },
                            react_1.default.createElement(react_2.ThreadPrimitive.Empty, null,
                                react_1.default.createElement("div", { className: 'ds-ma-welcome' },
                                    react_1.default.createElement(_ui_1.Text, { type: _ui_1.Text.TYPES.HEADLINE2 }, (0, util_1.t)('ASSISTANT.welcomeRow1', { x: me.firstName })),
                                    react_1.default.createElement(_ui_1.Text, { type: _ui_1.Text.TYPES.HEADLINE2, color: _ui_1.Text.COLORS.GRAY }, (0, util_1.t)('ASSISTANT.welcomeRow2')))),
                            react_1.default.createElement(react_2.ThreadPrimitive.Messages, { components: { UserMessage, AssistantMessage } }),
                            react_1.default.createElement(react_2.ThreadPrimitive.If, { running: true },
                                react_1.default.createElement("div", { className: 'ds-ma-working' },
                                    react_1.default.createElement(_ui_1.Loader, { size: _ui_1.Loader.SIZES.TINY })))),
                        (0, util_1.isValue)(activeBanner) && react_1.default.createElement(assistant_banner_1.AssistantBanner, { ...activeBanner }),
                        isPlanningVisible !== true && (react_1.default.createElement(assistant_quick_actions_1.AssistantQuickActions, { actions: quickActions, disabled: isComposerBlocked, onInsert: focusComposer })),
                        react_1.default.createElement(react_2.ComposerPrimitive.Root, { className: 'ds-ma-composer' },
                            react_1.default.createElement("div", { className: 'ds-ma-attachments' },
                                react_1.default.createElement(react_2.ComposerPrimitive.Attachments, { components: { Attachment: assistant_attachment_chip_1.AssistantAttachmentChip } })),
                            react_1.default.createElement("div", { className: (0, classnames_1.default)('ds-ma-composer-row', {
                                    'is-disabled': isComposerBlocked,
                                }) },
                                react_1.default.createElement(react_2.ComposerPrimitive.AddAttachment, { className: 'ds-ma-attach', disabled: isComposerBlocked, title: (0, util_1.t)('ATTACHMENT_UPLOAD') ?? undefined, "aria-label": (0, util_1.t)('ATTACHMENT_UPLOAD') ?? undefined },
                                    react_1.default.createElement(_ui_1.Icon, { ico: _ui_1.Icon.ICONS.attachment, color: _ui_1.Icon.COLORS.INHERIT })),
                                react_1.default.createElement(react_2.ComposerPrimitive.Input, { ref: composerInputRef, className: 'ds-ma-input', placeholder: (0, util_1.t)('ASSISTANT.employeePlaceholder') ?? undefined, rows: 1, disabled: isComposerBlocked, "data-testid": 'ai-input' }),
                                react_1.default.createElement(react_2.ThreadPrimitive.If, { running: false },
                                    react_1.default.createElement(react_2.ComposerPrimitive.Send, { className: 'ds-ma-send', disabled: isComposerBlocked, "data-testid": 'ai-send' },
                                        react_1.default.createElement(_ui_1.Icon, { ico: _ui_1.Icon.ICONS.send, color: _ui_1.Icon.COLORS.WHITE }))),
                                react_1.default.createElement(react_2.ThreadPrimitive.If, { running: true },
                                    react_1.default.createElement(react_2.ComposerPrimitive.Cancel, { className: 'ds-ma-send' },
                                        react_1.default.createElement(_ui_1.Icon, { ico: 'close4', color: _ui_1.Icon.COLORS.WHITE }))))),
                        (0, util_1.isValue)(usageWithLimit) && isUsagePanelOpen && (react_1.default.createElement(assistant_usage_panel_1.AssistantUsagePanel, { usage: usageWithLimit, panelRef: usagePanelRef, onClose: closeSurface, onBuyCredits: onBuyCredits })),
                        isHistoryOpen && (react_1.default.createElement(assistant_history_panel_1.AssistantHistoryPanel, { isLoading: history.isLoading, hasLoaded: history.hasLoaded, conversations: history.conversations, panelRef: historyPanelRef, onClose: closeSurface })),
                        react_1.default.createElement("div", { className: 'ds-ma-disclaimer' },
                            react_1.default.createElement(_ui_1.Text, { type: _ui_1.Text.TYPES.BODY_MEDIUM },
                                (0, util_1.t)('ASSISTANT.disclaimerLine1'),
                                " ",
                                (0, util_1.t)('ASSISTANT.disclaimerLine2')),
                            (0, util_1.isValue)(usageWithLimit) && (react_1.default.createElement(assistant_usage_ring_1.AssistantUsageRing, { usedPercent: usageWithLimit.usedPercent, used: usageWithLimit.used, limit: usageWithLimit.limit, isSpent: usageWithLimit.used >= usageWithLimit.limit, onToggle: () => toggleSurface('USAGE'), isPanelOpen: isUsagePanelOpen, triggerRef: usageRingRef })))))))));
};
// n8n's Simple Memory hard-deletes a chat session after 60 idle minutes (hardcoded
// upstream), so past that point the agent has forgotten the conversation still shown on
// screen. This wrapper mirrors the cutoff: once the idle limit passes it marks the session
// EXPIRED, which locks the composer and shows a notice with a "new conversation" button.
// It deliberately does NOT reset by itself — the manager keeps the dead thread on screen to
// read, and decides when to start over. Slightly under 60 so we never treat history the
// server may have just dropped as still live.
const SESSION_STALE_MS = 55 * 60 * 1000;
const ManagerAssistantChat = ({ onCollapse, railPrompt, onRailPromptConsumed, onComposerBlockedChange, isPlanningVisible, }) => {
    const dispatch = (0, store_1.useRootDispatch)();
    const me = (0, store_1.useRootSelector)((state) => state.me);
    const workspaceId = (0, store_1.useRootSelector)((state) => state.workspaceId);
    const userId = me?.id;
    // „Vyřešit" on an onboarding step parks the step here for us to pick up — see
    // reducer/onboarding.ts.
    const seedStep = (0, store_1.useRootSelector)((state) => (0, util_1.isValue)(workspaceId)
        ? state.onboarding[workspaceId]?.assistantSeedStep
        : undefined);
    // Bumping the epoch remounts ManagerAssistantChatSession (the key below).
    const [chatEpoch, setChatEpoch] = (0, react_1.useState)(0);
    const restoredChat = (0, react_1.useMemo)(() => {
        if (!(0, util_1.isValue)(userId) || !(0, util_1.isValue)(workspaceId)) {
            return {
                sessionId: (0, react_2.generateId)(),
                lastExchangeAt: null,
                messages: [],
            };
        }
        return ((0, manager_assistant_chat_persistence_1.loadManagerAssistantChat)({
            userId,
            workspaceId,
            staleMs: SESSION_STALE_MS,
        }) ?? {
            sessionId: (0, react_2.generateId)(),
            lastExchangeAt: null,
            messages: [],
        });
    }, [chatEpoch, userId, workspaceId]);
    // A thread we opened rather than restored. Takes precedence over `restoredChat` for as
    // long as it lives, which is until the next „new chat" — the session it describes is the
    // one that gets mounted, persisted and (on the next reload) restored like any other.
    const [seededChat, setSeededChat] = (0, react_1.useState)(null);
    const isSeeded = (0, util_1.isValue)(seededChat) && seededChat.workspaceId === workspaceId;
    const chatStart = isSeeded ? seededChat : restoredChat;
    const seedPrompt = isSeeded ? seededChat.prompt : undefined;
    // Drop the queued prompt once the session has sent it. Keeping it would resend on the next
    // mount of the same session — a workspace switch away and back remounts by key.
    const handleSeedConsumed = (0, react_1.useCallback)(() => {
        setSeededChat((seeded) => (0, util_1.isValue)(seeded) ? { ...seeded, prompt: undefined } : seeded);
    }, []);
    // A workspace switch retires the seeded thread for good. `isSeeded` already keeps it off
    // screen under another workspace, but without this it would still be sitting here on the
    // way back: A→B→A would select it again and mount the empty thread it describes instead of
    // the conversation it has since grown into — and the first new message would persist that
    // stub over the stored one. `restoredChat` reloads the real thread from storage.
    (0, react_1.useEffect)(() => {
        setSeededChat((seeded) => (0, util_1.isValue)(seeded) && seeded.workspaceId !== workspaceId ? null : seeded);
    }, [workspaceId]);
    // Last exchange with the agent — the only events that reset the server-side memory
    // TTL (typing/clicking doesn't). null until the first message, so an untouched chat
    // never resets.
    const lastExchangeRef = (0, react_1.useRef)(chatStart.lastExchangeAt);
    const [lastExchangeAt, setLastExchangeAt] = (0, react_1.useState)(chatStart.lastExchangeAt);
    (0, react_1.useEffect)(() => {
        lastExchangeRef.current = chatStart.lastExchangeAt;
        setLastExchangeAt(chatStart.lastExchangeAt);
    }, [chatStart.sessionId, chatStart.lastExchangeAt]);
    const [isExpired, setIsExpired] = (0, react_1.useState)(false);
    const handleExchange = (0, react_1.useCallback)(() => {
        const now = Date.now();
        lastExchangeRef.current = now;
        setLastExchangeAt(now);
        return now;
    }, []);
    // Expire on a timer rather than on the next interaction, so the notice is already there
    // when the manager comes back to the tab instead of appearing under their cursor.
    (0, react_1.useEffect)(() => {
        if (!(0, util_1.isValue)(lastExchangeAt)) {
            setIsExpired(false);
            return;
        }
        const remaining = lastExchangeAt + SESSION_STALE_MS - Date.now();
        if (remaining <= 0) {
            setIsExpired(true);
            return;
        }
        setIsExpired(false);
        const timer = window.setTimeout(() => setIsExpired(true), remaining);
        return () => window.clearTimeout(timer);
    }, [lastExchangeAt]);
    // Drop the stored conversation and remount the session — the only way out of the expired
    // state, triggered by the header's "new chat" button or the one in the expiry notice.
    const handleNewChat = (0, react_1.useCallback)(() => {
        if ((0, util_1.isValue)(userId) && (0, util_1.isValue)(workspaceId)) {
            (0, manager_assistant_chat_persistence_1.clearManagerAssistantChat)(userId, workspaceId);
        }
        lastExchangeRef.current = null;
        setIsExpired(false);
        setSeededChat(null);
        setChatEpoch((epoch) => epoch + 1);
    }, [userId, workspaceId]);
    // Same restart, but with the onboarding step's message queued as the manager's first
    // turn. Fires on every request, so a repeat click on the SAME step also starts over — and
    // a request made while a reply is still streaming wins, because the remount aborts the run.
    (0, react_1.useEffect)(() => {
        // `userId` too: without it the stored chat can neither be cleared nor written, so
        // seeding now would show the new thread while the OLD one stays in storage, ready to
        // come back on the next reload. The seed stays parked until `me` has loaded.
        if (!(0, util_1.isValue)(seedStep) || !(0, util_1.isValue)(workspaceId) || !(0, util_1.isValue)(userId))
            return;
        if ((0, onboarding_assistant_prompt_1.isOnboardingAssistantStep)(seedStep)) {
            const prompt = (0, onboarding_assistant_prompt_1.buildOnboardingAssistantPrompt)(seedStep);
            (0, manager_assistant_chat_persistence_1.clearManagerAssistantChat)(userId, workspaceId);
            setIsExpired(false);
            setSeededChat({
                workspaceId,
                sessionId: (0, react_2.generateId)(),
                // Left null on purpose: the send below is a real exchange, so `onExchange` stamps
                // this the moment the request goes out. An untranslated step sends nothing, and
                // then a thread with no exchange correctly has no staleness clock running.
                lastExchangeAt: null,
                onboardingStep: seedStep,
                messages: [],
                ...((0, util_1.isValue)(prompt) ? { prompt } : {}),
            });
            setChatEpoch((epoch) => epoch + 1);
        }
        // Cleared even when there was nothing to seed: this slice is persisted, so a step an
        // older build wrote and this one doesn't know would sit here re-firing forever.
        dispatch((0, onboarding_1.clearedOnboardingAssistantSeed)(workspaceId));
    }, [seedStep, workspaceId, userId, dispatch]);
    // The same restart for a prompt another page asked for (a dashboard alert's „Vyřešit s
    // Daysie"). Not an onboarding thread, so no step is attached.
    const askedPrompt = (0, store_1.useRootSelector)((state) => state.managerAssistant.seedPrompt);
    (0, react_1.useEffect)(() => {
        if (!(0, util_1.isValue)(askedPrompt) || !(0, util_1.isValue)(workspaceId) || !(0, util_1.isValue)(userId))
            return;
        (0, manager_assistant_chat_persistence_1.clearManagerAssistantChat)(userId, workspaceId);
        setIsExpired(false);
        setSeededChat({
            workspaceId,
            sessionId: (0, react_2.generateId)(),
            lastExchangeAt: null,
            messages: [],
            prompt: askedPrompt,
        });
        setChatEpoch((epoch) => epoch + 1);
        dispatch((0, manager_assistant_1.consumedManagerAssistantSeed)());
    }, [askedPrompt, workspaceId, userId, dispatch]);
    // Backstop for the timer above: a background tab can have its timers throttled, and one
    // scheduled before the machine slept won't fire on time. Capture phase, so this runs
    // before the click or focus reaches a thread the agent no longer remembers.
    const handleInteraction = (0, react_1.useCallback)(() => {
        const last = lastExchangeRef.current;
        if ((0, util_1.isValue)(last) && Date.now() - last > SESSION_STALE_MS) {
            setIsExpired(true);
        }
    }, []);
    return (react_1.default.createElement("div", { className: 'ds-ma-session-boundary', onPointerDownCapture: handleInteraction, onFocusCapture: handleInteraction },
        react_1.default.createElement(ManagerAssistantChatSession, { key: `${chatEpoch}-${chatStart.sessionId}`, onCollapse: onCollapse, railPrompt: railPrompt, onRailPromptConsumed: onRailPromptConsumed, onComposerBlockedChange: onComposerBlockedChange, isPlanningVisible: isPlanningVisible, onNewChat: handleNewChat, onExchange: handleExchange, sessionId: chatStart.sessionId, initialMessages: chatStart.messages, lastExchangeAt: lastExchangeAt, isExpired: isExpired, userId: (0, util_1.isValue)(userId) ? userId : null, workspaceId: (0, util_1.isValue)(workspaceId) ? workspaceId : null, onboardingStep: chatStart.onboardingStep, seedPrompt: seedPrompt, onSeedConsumed: handleSeedConsumed })));
};
exports.ManagerAssistantChat = ManagerAssistantChat;
exports.default = exports.ManagerAssistantChat;
